import { Fragment, type ReactNode } from 'react';
import { cx } from '../cx';
import { CHART_MINIMUM } from './scale';

/**
 * The one thin-data look (guideline 12, "Uncertainty and thin data"). Readers ignore sample sizes
 * unless they can see them, so every chart that shows a median or a spread uses the same three
 * tiers: `full` (20 or more values: median and middle half), `rough` (10 to 19: a dashed, hollow
 * median and the word "rough"), `none` (under 10: no figure, "too few ads").
 */
export type SampleTier = 'full' | 'rough' | 'none';

export function sampleTier(sample: number): SampleTier {
  if (sample >= CHART_MINIMUM.spread) return 'full';
  if (sample >= CHART_MINIMUM.median) return 'rough';
  return 'none';
}

/** The words for the thin tiers, translated by the page ("rough", "Too few ads"). */
export type ThinLabels = { rough: ReactNode; tooFew: ReactNode };

/** A rough mark: hollow, dashed, so it never reads as a confident figure. */
export const ROUGH_DOT = 'rounded-full border-2 border-dashed border-jc-ink bg-jc-surface';
/** A rough bar or cell: fainter, with a dashed outline. */
export const ROUGH_FILL = 'opacity-45 outline-1 outline-dashed outline-jc-ink-secondary -outline-offset-1';

/** "n=12" on the mark: the sample size, where the reader looks. */
export function SampleCount({ n, className }: { n: number; className?: string }) {
  return <span className={cx('text-xs text-jc-ink-secondary tabular-nums', className)}>n={n}</span>;
}

/** The legend explaining the tiers once, under any chart that has thin rows (labels translated). */
export function ThinDataLegend({
  full,
  rough,
  none,
  narrow = false,
}: {
  full: ReactNode;
  rough: ReactNode;
  none: ReactNode;
  /** Stack the three entries, one per line (a tight column). By default they wrap with the legend row. */
  narrow?: boolean;
}) {
  const Wrap = narrow ? 'span' : Fragment;
  return (
    <Wrap {...(narrow ? { className: 'flex flex-col items-start gap-1' } : {})}>
      <span className="inline-flex items-center gap-1.5">
        <span className="h-2 w-5 rounded-jc-info bg-jc-chart-context" />
        {full}
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className={cx('size-2.5', ROUGH_DOT)} />
        {rough}
      </span>
      <span className="inline-flex items-center gap-1.5">{none}</span>
    </Wrap>
  );
}
